import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { getBranchOwner, post, api, get } from '@config';
import Loading from '../../../../_components/Loading';

// Animações
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Estilos
const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 28px 16px;
  }

  @media (max-width: 480px) {
    padding: 20px 12px 80px;
  }
`;

const HeroSection = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  background: #fff;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 28px;
    gap: 24px;
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const HeroContent = styled.div`
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  max-width: 400px;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    max-width: 320px;
  }

  @media (max-width: 480px) {
    max-width: 260px;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: #101828;
  margin-bottom: 16px;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #667085;
  margin-bottom: 32px;
  line-height: 1.6;
`;

const Badge = styled.span`
  background: #f0f9ff;
  color: #0088cc;
  padding: 6px 12px;
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 16px;
  display: inline-block;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FeatureCard = styled.div`
  background: #fff;
  padding: 28px;
  border-radius: 16px;
  border: 1px solid #eaecf0;
  transition: all 0.3s ease;

  &:hover {
    border-color: #0088cc;
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  background: ${props => props.bgColor || '#f0f9ff'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: ${props => props.color || '#0088cc'};
  font-size: 1.4rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: #101828;
  margin-bottom: 8px;
`;

const FeatureText = styled.p`
  font-size: 0.9rem;
  color: #667085;
  line-height: 1.6;
`;

const ActionButton = styled.button`
  background: #0088cc;
  color: #fff;
  border: none;
  padding: 18px 36px;
  font-size: 1.15rem;
  font-weight: 700;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 14px rgba(0, 136, 204, 0.4);

  &:hover:not(:disabled) {
    background: #0077b3;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 136, 204, 0.5);
  }

  &:disabled {
    background: #98a2b3;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
`;

const AIHighlightCard = styled.div`
  background: linear-gradient(135deg, #f0f4ff 0%, #e8edff 100%);
  border: 1px solid #d6dfff;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  display: flex;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
  }
`;

const AIHighlightIcon = styled.div`
  width: 56px;
  height: 56px;
  background: #0088cc;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
  flex-shrink: 0;
  animation: ${pulse} 2s infinite;
`;

const AIHighlightContent = styled.div`
  flex: 1;
`;

const AIHighlightTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: #101828;
  margin-bottom: 8px;
`;

const AIHighlightText = styled.p`
  font-size: 1rem;
  color: #475467;
  line-height: 1.6;
  margin-bottom: 4px;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 32px 0;
  color: #98a2b3;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #eaecf0;
  }
`;

const EnableNfse = () => {
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [cityCompatibility, setCityCompatibility] = useState({ loading: true, is_compatible: false, city_name: '' });
  const branch = getBranchOwner();

  // Se já tem permissão 3 no banco, vai direto para o emissor/configurações
  const hasPermission = branch && branch.permissions && branch.permissions.nfse === 3;

  const updateBranchNfsePermission = (status = 3) => {
    try {
      const currentBranch = getBranchOwner();
      if (currentBranch) {
        if (!currentBranch.permissions) currentBranch.permissions = {};
        currentBranch.permissions.nfse = status;
        window.localStorage.setItem('easygestorweb_current_branch', JSON.stringify(currentBranch));
      }
    } catch (e) {
      console.warn("Erro ao sincronizar permissao de NFSe no localStorage", e);
    }
  };

  const checkCityCompatibility = () => {
    if (!branch) return;
    const url = `${api.reports.requestPermissions}check-city-compatibility/?branch_owner=${branch.id}`;
    get(
      url,
      (data) => {
        setCityCompatibility({ 
          loading: false, 
          is_compatible: data.is_compatible, 
          city_name: data.city_name 
        });
        if (data.is_compatible) {
          updateBranchNfsePermission(3);
        }
      },
      () => setCityCompatibility({ loading: false, is_compatible: false, city_name: '' })
    );
  };

  const dispatchLeadAndGoToConfig = () => {
    if (!branch) return;
    setLoading(true);
    const url = `${api.reports.requestPermissions}?branch_owner=${branch.id}`;
    
    post(
      url, 
      { 
        solicited_module: 'nfse', 
        notes: 'Primeiro acesso à emissão de NFS-e - Lead automático de boas-vindas' 
      }, 
      (resp) => {
        if ((resp && resp.status === 3) || cityCompatibility.is_compatible) {
          updateBranchNfsePermission(3);
        }
        window.location.href = '/nfse/settings';
      }, 
      (err) => {
        if (cityCompatibility.is_compatible) {
          updateBranchNfsePermission(3);
        }
        window.location.href = '/nfse/settings';
      }
    );
  };

  useEffect(() => {
    checkCityCompatibility();
    setInitializing(false);
  }, []);

  // Se já tem permissão no banco, redireciona para o emissor (não mostra intro)
  if (hasPermission) {
    window.location.href = '/nfse';
    return null;
  }

  if (initializing) {
    return <Loading />;
  }

  const cityName = (branch && branch.address && branch.address.city_location && branch.address.city_location.city_name) || 'sua localidade';

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <Badge>Novo Módulo</Badge>
            {!cityCompatibility.loading && cityCompatibility.is_compatible && (
              <Badge style={{ background: '#ecfdf3', color: '#12b76a' }}>
                <i className="fas fa-check-circle" style={{ marginRight: '4px' }}></i>
                {cityCompatibility.city_name} Homologada
              </Badge>
            )}
            {!cityCompatibility.loading && !cityCompatibility.is_compatible && (
              <Badge style={{ background: '#fff4ed', color: '#d97706' }}>
                <i className="fas fa-clock" style={{ marginRight: '4px' }}></i>
                {cityName} - Verificando compatibilidade
              </Badge>
            )}
          </div>
          <Title>Emita NFS-e com Inteligência Artificial</Title>
          <Subtitle>
            Automatize a emissão de Notas Fiscais de Serviço Eletrônicas diretamente pelo EasyGestor. 
            <strong>Grave áudios, anexe contratos ou orçamentos</strong> e deixe a IA preencher tudo para você.
            Economize tempo, evite erros manuais e mantenha sua empresa em dia com o fisco.
          </Subtitle>
          
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px' }}>
            <ActionButton onClick={dispatchLeadAndGoToConfig} disabled={loading}>
              {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-rocket"></i>}
              Começar a Emitir NFS-e
            </ActionButton>
          </div>
        </HeroContent>
        <HeroImage src="/nfse_module.png" alt="NFS-e Module" />
      </HeroSection>

      {/* Destaque IA */}
      <AIHighlightCard>
        <AIHighlightIcon>
          <i className="fas fa-magic"></i>
        </AIHighlightIcon>
        <AIHighlightContent>
          <AIHighlightTitle>Preenchimento Inteligente com IA</AIHighlightTitle>
          <AIHighlightText>
            <strong>Diga ou envie o que precisa</strong> — a IA entende áudios (WhatsApp), PDFs, imagens de contratos, 
            orçamentos e notas fiscais, e preenche automaticamente: tomador, serviço, valores, impostos e observações.
          </AIHighlightText>
          <AIHighlightText>
            Basta clicar em <strong>"Preenchimento Inteligente com IA"</strong> na tela de emissão, escolher a entrada 
            (gravação, upload ou texto) e revisar o resultado antes de emitir.
          </AIHighlightText>
        </AIHighlightContent>
      </AIHighlightCard>

      <Divider>Principais Recursos</Divider>

      <FeatureGrid>
        <FeatureCard>
          <IconWrapper bgColor="#f0f9ff" color="#0088cc"><i className="fas fa-microphone-alt"></i></IconWrapper>
          <FeatureTitle>Emissão por Áudio (WhatsApp)</FeatureTitle>
          <FeatureText>Grave ou envie áudios do WhatsApp e a IA extrai todos os dados da nota automaticamente.</FeatureText>
        </FeatureCard>
        <FeatureCard>
          <IconWrapper bgColor="#ecfdf3" color="#12b76a"><i className="fas fa-file-import"></i></IconWrapper>
          <FeatureTitle>Importação de Documentos</FeatureTitle>
          <FeatureText>Faça upload de PDFs, imagens ou contratos — a IA lê e preenche tomador, serviço e valores.</FeatureText>
        </FeatureCard>
        <FeatureCard>
          <IconWrapper bgColor="#fff4ed" color="#f59e0b"><i className="fas fa-bolt"></i></IconWrapper>
          <FeatureTitle>Emissão Instantânea</FeatureTitle>
          <FeatureText>Emita suas notas em poucos cliques, com preenchimento automático e cálculo de impostos em tempo real.</FeatureText>
        </FeatureCard>
        <FeatureCard>
          <IconWrapper bgColor="#fef3f2" color="#d92d20"><i className="fas fa-shield-alt"></i></IconWrapper>
          <FeatureTitle>Segurança Fiscal</FeatureTitle>
          <FeatureText>Cálculo automático de impostos (ISS, IBS, CBS) e integração direta com centenas de prefeituras via InvoiCy.</FeatureText>
        </FeatureCard>
        <FeatureCard>
          <IconWrapper bgColor="#f5f0ff" color="#8b5cf6"><i className="fas fa-sync"></i></IconWrapper>
          <FeatureTitle>Gestão Centralizada</FeatureTitle>
          <FeatureText>Controle todas as suas notas emitidas, canceladas e em processamento em um único painel intuitivo.</FeatureText>
        </FeatureCard>
        <FeatureCard>
          <IconWrapper bgColor="#ecfdf3" color="#06b6d4"><i className="fas fa-mobile-alt"></i></IconWrapper>
          <FeatureTitle>Mobile-First</FeatureTitle>
          <FeatureText>Emita e gerencie suas NFS-e de qualquer lugar, com interface responsiva otimizada para celular.</FeatureText>
        </FeatureCard>
      </FeatureGrid>

      <div style={{ textAlign: 'center', marginTop: '40px', padding: '24px', background: '#f9fafb', borderRadius: '12px', border: '1px solid #eaecf0' }}>
        <p style={{ color: '#667085', fontSize: '0.95rem', marginBottom: '12px' }}>
          <i className="fas fa-lightbulb" style={{ color: '#f59e0b', marginRight: '8px' }}></i>
          <strong>Dica:</strong> Configure sua Inscrição Municipal e credenciais em <strong>Configurações</strong> antes da primeira emissão.
        </p>
        <ActionButton onClick={dispatchLeadAndGoToConfig} disabled={loading} style={{ background: '#28a745', boxShadow: '0 4px 14px rgba(40, 167, 69, 0.4)' }}>
          <i className="fas fa-cog"></i> Ir para Configurações
        </ActionButton>
      </div>
    </Container>
  );
};

export default EnableNfse;


// WEBPACK FOOTER //
// ./src/views/Fiscal/NFSe/enable/index.jsx